import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  ButtonGroup,
} from "reactstrap";
import { usePostHog } from "@posthog/react";

import { useSeo } from "../hooks/useSeo";
import { fetchGameRound, submitGameAnswer } from "../hooks/useGame";
import { WINDOW_LABELS_LONG } from "../utils/windows";
import {
  ASSET_CLASS_LABELS,
  analogStats,
  article,
  displayName,
  pct,
  pctOrDash,
  similarityPct,
} from "../utils/game";
import GameChart from "./GameChart";
import GameChartToggle, { useGameChartMode } from "./GameChartToggle";
import ForwardCurveFanChart from "./ForwardCurveFanChart";
import GameDailySet from "./GameDailySet";

type GameMode = "daily" | "endless";

// Short windows only — NOT the app-wide WINDOW_ORDER. /api/game/* is unauthenticated and the
// long windows (6m/1y) are Pro-only, so the backend pool excludes them (GAME_WINDOWS in
// scripts/game_rounds.py) and 400s on a long window; these buttons must match it.
const GAME_WINDOWS = ["10", "20", "65"];

function Tally({ tally }: { tally: GameTally }) {
  if (tally.rounds === 0) return null;
  return (
    <div style={{ fontFamily: "var(--font-mono)" }}>
      You <strong>{tally.you}</strong>/{tally.rounds}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Col xs={6} md={3}>
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{value}</div>
    </Col>
  );
}

/**
 * Game — "Call the Chart".
 *
 * The player sees a blind, z-normalized price window and calls up or down; the reveal shows
 * what actually happened next, plus what followed the closest-matching windows in 20 years of
 * history. Rounds are drawn from real historical signals with no outcome-dependent selection
 * (see the api repo's docs/prediction_game.md).
 *
 * The game demonstrates historical MATCHING, not prediction — deliberately. No engine hit rate
 * is shown anywhere here (no per-round verdict, no running analog tally): the archive audit
 * found no cut of the track record that beats its matched null, so a "the analogs got it right"
 * scoreboard would be an unsupportable performance claim. The reveal shows the two things that
 * ARE supportable: how closely the retrieved windows resemble this one (`top_similarity`), and
 * the DISPERSION of what followed them — median, middle half, worst, best. Not their majority
 * direction, which is exactly the claim the audit could not separate from its matched null.
 */
export default function Game() {
  useSeo({
    title: "Call the Chart — DejaChart Chart Game",
    description:
      "Call a real historical chart up or down, then see what actually happened — and what followed the most similar windows in 20 years of market history.",
    path: "/game",
  });

  const posthog = usePostHog();
  // Daily is the default landing experience (G2): a fixed, shareable 5-round set. Endless
  // is the original G1 loop, kept for players who want to keep going past the daily 5.
  const [mode, setMode] = useState<GameMode>("daily");
  const [round, setRound] = useState<GameBlindRound | null>(null);
  const [result, setResult] = useState<GameAnswerResult | null>(null);
  const [guess, setGuess] = useState<GameDirection | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [windowFilter, setWindowFilter] = useState<number | null>(null);
  const [seen, setSeen] = useState<number[]>([]);
  const [tally, setTally] = useState<GameTally>({ rounds: 0, you: 0 });
  const [chartMode, setChartMode] = useGameChartMode();

  // `seen` is read but deliberately NOT a dependency: including it would reload the round
  // every time a completed round is appended, throwing away the chart the player is on.
  const loadRound = useCallback(
    async (exclude: number[]) => {
      setLoading(true);
      setError(null);
      setResult(null);
      setGuess(null);
      try {
        const next = await fetchGameRound({ window: windowFilter, exclude });
        setRound(next);
      } catch {
        setError("Couldn't load a chart. Try again in a moment.");
        setRound(null);
      } finally {
        setLoading(false);
      }
    },
    [windowFilter],
  );

  useEffect(() => {
    // Endless mode's pool fetch only runs in endless mode — daily mode (GameDailySet)
    // has its own data source, and firing this unconditionally on mount would burn an
    // unauthenticated /api/game/round call nobody's about to see.
    if (mode !== "endless") return;
    // Reset the run when the window filter changes — a tally mixing horizons is meaningless.
    setSeen([]);
    setTally({ rounds: 0, you: 0 });
    void loadRound([]);
  }, [loadRound, mode]);

  const answer = async (direction: GameDirection) => {
    if (!round || result || submitting) return;
    setSubmitting(true);
    setGuess(direction);
    try {
      const res = await submitGameAnswer(round.round_token, direction);
      setResult(res);
      setSeen(prev => [...prev, round.round_id]);
      setTally(prev => ({
        rounds: prev.rounds + 1,
        you: prev.you + (res.correct ? 1 : 0),
      }));
      // engine_correct is captured for the G4 dataset only — it is never rendered.
      posthog.capture("game_round_answered", {
        window: round.window,
        asset_class: round.asset_class,
        correct: res.correct,
        engine_correct: res.engine_correct,
      });
    } catch {
      setError("Couldn't score that answer. Try the next chart.");
      setGuess(null);
    } finally {
      setSubmitting(false);
    }
  };

  // ↑/↓ as a shortcut for the two buttons. The listener is global (this is a dedicated
  // page, so arrow keys have no better job here) but only swallows the keypress while a
  // round is actually open — otherwise it would break scrolling on the reveal.
  const answerRef = useRef(answer);
  answerRef.current = answer;
  const canAnswerRef = useRef(false);
  canAnswerRef.current = Boolean(round) && !result && !submitting;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      if (e.metaKey || e.ctrlKey || e.altKey || !canAnswerRef.current) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      e.preventDefault();
      void answerRef.current(e.key === "ArrowUp" ? "up" : "down");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const assetLabel = round ? ASSET_CLASS_LABELS[round.asset_class] ?? round.asset_class : "";
  const stats = analogStats(result?.analog_curve);
  const name = result ? displayName(result.ticker, result.ticker_name) : null;

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={9}>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <h1 className="h3 mb-1">Call the Chart</h1>
              <p className="mb-0" style={{ color: "var(--text-muted)" }}>
                Call the next move. Then see what happened — and what followed the charts that
                looked just like it.
              </p>
            </div>
            {mode === "endless" && <Tally tally={tally} />}
          </div>

          <ButtonGroup size="sm" className="mb-3">
            <Button color="primary" outline={mode !== "daily"} onClick={() => setMode("daily")}>
              Daily 5
            </Button>
            <Button color="primary" outline={mode !== "endless"} onClick={() => setMode("endless")}>
              Endless
            </Button>
          </ButtonGroup>

          {mode === "daily" && <GameDailySet />}

          {mode === "endless" && (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <ButtonGroup size="sm">
                  <Button
                    color="secondary"
                    outline={windowFilter !== null}
                    onClick={() => setWindowFilter(null)}
                  >
                    Any
                  </Button>
                  {GAME_WINDOWS.map(key => {
                    const w = Number(key);
                    return (
                      <Button
                        key={key}
                        color="secondary"
                        outline={windowFilter !== w}
                        onClick={() => setWindowFilter(w)}
                      >
                        {WINDOW_LABELS_LONG[w]}
                      </Button>
                    );
                  })}
                </ButtonGroup>
                <GameChartToggle
                  mode={chartMode}
                  onChange={setChartMode}
                  available={Boolean(round?.ohlc)}
                />
              </div>

              <Card className="mb-3">
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : error && !round ? (
                    <div className="text-center py-5" style={{ color: "var(--text-muted)" }}>
                      <p className="mb-3">{error}</p>
                      <Button color="primary" onClick={() => void loadRound(seen)}>
                    Retry
                      </Button>
                    </div>
                  ) : round ? (
                    <>
                      <div
                        className="d-flex justify-content-between mb-2"
                        style={{ fontSize: 13, color: "var(--text-muted)" }}
                      >
                        <span>
                          {assetLabel} · {round.points} bars of history
                        </span>
                        <span>
                      Where does it go over the next {round.horizon} trading{" "}
                          {round.horizon === 1 ? "day" : "days"}?
                        </span>
                      </div>

                      <GameChart
                        series={round.series}
                        ohlc={round.ohlc}
                        forward={result?.forward_series}
                        forwardOhlc={result?.forward_ohlc}
                        realized={result?.realized_direction}
                        mode={chartMode}
                        forwardPoints={round.horizon}
                        onCall={result || submitting ? undefined : direction => void answer(direction)}
                      />

                      {!result ? (
                        <>
                          <div className="d-flex justify-content-center gap-3 mt-3">
                            <Button
                              color="success"
                              size="lg"
                              disabled={submitting}
                              onClick={() => void answer("up")}
                            >
                          ▲ Up
                            </Button>
                            <Button
                              color="danger"
                              size="lg"
                              disabled={submitting}
                              onClick={() => void answer("down")}
                            >
                          ▼ Down
                            </Button>
                          </div>
                          <div
                            className="text-center mt-2"
                            style={{ fontSize: 12, color: "var(--text-muted)" }}
                          >
                        or click the chart — ↑ / ↓ keys work too
                          </div>
                        </>
                      ) : (
                        <div className="mt-3">
                          {/* The player's call is the only thing that gets a verdict here. The
                          analogs are a separate panel below, and they get facts, not a score —
                          never render engine_correct; see the component doc comment. */}
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <Badge color={result.correct ? "success" : "danger"} pill>
                          You said {guess === "up" ? "up" : "down"} —{" "}
                              {result.correct ? "correct" : "wrong"}
                            </Badge>
                          </div>
                          <p className="mb-2">
                            <strong>{result.ticker}</strong>
                            {name !== null && (
                              <>
                                {" "}
                                <span style={{ color: "var(--text-muted)" }}>{name}</span>
                              </>
                            )}{" "}
                        on {result.signal_date} — moved{" "}
                            <strong
                              style={{
                                color: result.realized_return >= 0 ? "var(--success)" : "var(--danger)",
                              }}
                            >
                              {pct(result.realized_return)}
                            </strong>{" "}
                        over the next {result.horizon} trading{" "}
                            {result.horizon === 1 ? "day" : "days"}.
                          </p>
                          <Button
                            color="primary"
                            onClick={() => void loadRound([...seen, round.round_id])}
                          >
                        Next chart →
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5" style={{ color: "var(--text-muted)" }}>
                  No charts available for this window yet.
                    </div>
                  )}
                </CardBody>
              </Card>

              {result?.analog_curve && (
                <Card className="mb-3">
                  <CardBody>
                    <h2 className="h6 mb-1">What followed the closest historical matches</h2>
                    <p className="mb-0" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                      {stats
                        ? `${stats.count} windows in 20 years of history looked most like this one`
                        : "Past windows that looked like this one"}
                      {typeof result.top_similarity === "number" &&
                    ` — the closest is ${article(similarityPct(result.top_similarity))} ${similarityPct(result.top_similarity)}% shape match`}
                  .
                    </p>

                    {stats && (
                      <Row className="g-2 my-3">
                        <Stat label="Median" value={pctOrDash(stats.median)} />
                        <Stat
                          label="Middle half"
                          value={`${pctOrDash(stats.p25)} to ${pctOrDash(stats.p75)}`}
                        />
                        <Stat label="Worst" value={pctOrDash(stats.worst)} />
                        <Stat label="Best" value={pctOrDash(stats.best)} />
                      </Row>
                    )}
                    <ForwardCurveFanChart curve={result.analog_curve} showWinRate={false} />
                  </CardBody>
                </Card>
              )}

              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Charts are real historical windows drawn at random from our signal archive —
                never picked by how they turned out. DejaChart finds the windows in 20 years of
                history that most resemble the one in front of you and shows what followed them.
                For entertainment and education only; not investment advice.
              </p>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
